import React, { useState, useEffect } from 'react';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { ALL_TABS, ROLE_DEFAULTS, DASHBOARD_WIDGETS } from '../../config/permissions';

const CREATE_FN = 'https://vtfnxhwyfcvpdpfcmuyp.supabase.co/functions/v1/create-staff-user';
const MANAGE_FN = 'https://vtfnxhwyfcvpdpfcmuyp.supabase.co/functions/v1/manage-staff-user';

const SECTIONS = [...new Set(ALL_TABS.map(t => t.section))];

function PermissionsChecklist({ value, onChange }) {
  const toggle = (key) => {
    const next = value.includes(key) ? value.filter(k => k !== key) : [...value, key];
    onChange(next);
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px 20px', marginTop: 8 }}>
      {SECTIONS.map(section => (
        <div key={section}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{section}</div>
          {ALL_TABS.filter(t => t.section === section).map(t => (
            <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 3, cursor: 'pointer' }}>
              <input type="checkbox" checked={value.includes(t.key)} onChange={() => toggle(t.key)} />
              {t.label}
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function WidgetChecklist({ value, onChange, liabilityAccounts }) {
  const toggle = (key) => {
    const next = value.includes(key) ? value.filter(k => k !== key) : [...value, key];
    onChange(next);
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px' }}>
        {DASHBOARD_WIDGETS.map(w => (
          <label key={w.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={value.includes(w.key)} onChange={() => toggle(w.key)} />
            {w.label}
          </label>
        ))}
      </div>
      {liabilityAccounts.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            Individual Liability Accounts
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px 20px' }}>
            {liabilityAccounts.map(a => {
              const key = `account:${a.id}`;
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={value.includes(key)} onChange={() => toggle(key)} />
                  {a.account_code} — {a.account_name}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function UserList() {
  const { user, role } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';

  if (role !== 'sadmin' && role !== 'admin') {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Access Restricted</div>
        <div style={{ fontSize: 14 }}>Only administrators can manage users.</div>
      </div>
    );
  }

  const [staff, setStaff]       = useState([]);
  const [agents, setAgents]     = useState([]);
  const [branches, setBranches] = useState([]);
  const [liabilityAccounts, setLiabilityAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState({ display_name: '', username: '', password: '', role: 'staff', agent_ref_id: '', branch_id: '', tab_permissions: ROLE_DEFAULTS.staff, dashboard_widgets: DASHBOARD_WIDGETS.map(w => w.key) });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');
  const [showPerms, setShowPerms]   = useState(false);
  const [showWidgets, setShowWidgets] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { load(); loadAgents(); loadBranches(); loadLiabilityAccounts(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('staff').select('*, agents(tls_code, full_name), branches(name, code)').order('display_name');
    setStaff(data || []);
    setLoading(false);
  }

  async function loadAgents() {
    const { data } = await supabase.from('agents').select('id, tls_code, full_name').order('tls_code');
    setAgents(data || []);
  }

  async function loadBranches() {
    const { data } = await supabase.from('branches').select('id, name, code').eq('is_active', true).order('name');
    setBranches(data || []);
  }

  async function loadLiabilityAccounts() {
    const { data } = await supabase
      .from('chart_of_accounts')
      .select('id, account_code, account_name, account_types!inner(category)')
      .eq('is_active', true)
      .eq('account_types.category', 'LIABILITY')
      .order('account_code');
    setLiabilityAccounts(data || []);
  }

  async function getToken() {
    const session = await supabase.auth.getSession();
    return session.data.session?.access_token;
  }

  async function handleAdd() {
    setError(''); setSuccess('');
    if (!form.display_name || !form.username || !form.password) {
      setError('Name, username and password are required.'); return;
    }
    const email = form.username.trim().toLowerCase() + '@tls.internal';
    if (form.role === 'agent' && !form.agent_ref_id) {
      setError('Select the agent record for this user.'); return;
    }
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(CREATE_FN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email, password: form.password, display_name: form.display_name }),
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error || 'Failed to create user.'); setSaving(false); return; }

      await supabase.from('staff').insert({
        email,
        display_name: form.display_name,
        role: form.role,
        agent_ref_id: form.role === 'agent' ? form.agent_ref_id : null,
        branch_id: form.branch_id || null,
        auth_user_id: json.id || null,
        tab_permissions: form.role === 'sadmin' ? null : form.tab_permissions,
        dashboard_widgets: form.dashboard_widgets,
        is_active: true,
        created_by: username,
      });

      setSuccess(`${form.display_name} added.`);
      setForm({ display_name: '', username: '', password: '', role: 'staff', agent_ref_id: '', branch_id: '', tab_permissions: ROLE_DEFAULTS.staff, dashboard_widgets: DASHBOARD_WIDGETS.map(w => w.key) });
      setShowAdd(false);
      load();
    } catch (e) { setError(String(e)); }
    setSaving(false);
  }

  async function toggleActive(s) {
    await supabase.from('staff').update({ is_active: !s.is_active }).eq('id', s.id);
    load();
  }

  function startEdit(s) {
    setEditId(s.id);
    setEditForm({
      display_name: s.display_name,
      username: s.email.replace('@tls.internal', ''),
      password: '',
      role: s.role,
      agent_ref_id: s.agent_ref_id || '',
      branch_id: s.branch_id || '',
      tab_permissions: s.tab_permissions || ROLE_DEFAULTS[s.role] || ROLE_DEFAULTS.staff,
      dashboard_widgets: s.dashboard_widgets || DASHBOARD_WIDGETS.map(w => w.key),
      action_passcode: s.action_passcode || '',
      void_passcode: s.void_passcode || '',
      personal_email: s.personal_email || '',
    });
    setShowPerms(false);
    setShowWidgets(false);
    setEditError('');
  }

  async function saveEdit(s) {
    setEditError('');
    if (!editForm.display_name.trim()) { setEditError('Name is required.'); return; }
    setEditSaving(true);
    try {
      const newRole = editForm.role || s.role;
      const newUsername = (editForm.username || '').trim().toLowerCase();
      const newEmail = newUsername ? newUsername + '@tls.internal' : s.email;
      const { error: dbErr } = await supabase.from('staff')
        .update({
          display_name: editForm.display_name.trim(),
          email: newEmail,
          role: newRole,
          agent_ref_id: newRole === 'agent' ? (editForm.agent_ref_id || s.agent_ref_id || null) : null,
          branch_id: editForm.branch_id || null,
          tab_permissions: newRole === 'sadmin' ? null : editForm.tab_permissions,
          dashboard_widgets: editForm.dashboard_widgets,
          action_passcode: editForm.action_passcode?.trim() || null,
          void_passcode: editForm.void_passcode?.trim() || null,
          personal_email: editForm.personal_email?.trim() || null,
        })
        .eq('id', s.id);
      if (dbErr) { setEditError(dbErr.message); setEditSaving(false); return; }

      // Only call edge function if email or password actually changed
      const emailChanged = newEmail && newEmail !== s.email;
      if (s.auth_user_id && (emailChanged || editForm.password)) {
        const token = await getToken();
        const payload = { action: 'update', auth_user_id: s.auth_user_id, display_name: editForm.display_name.trim() };
        if (emailChanged) payload.email = newEmail;
        if (editForm.password) payload.password = editForm.password;
        const res = await fetch(MANAGE_FN, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok) { setEditError(json.error || 'Auth update failed.'); setEditSaving(false); return; }
      }

      setEditId(null);
      setSuccess(`${editForm.display_name} updated.`);
      load();
    } catch (e) { setEditError(String(e)); }
    setEditSaving(false);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      if (deleteTarget.auth_user_id) {
        const token = await getToken();
        const res = await fetch(MANAGE_FN, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ action: 'delete', auth_user_id: deleteTarget.auth_user_id }),
        });
        const json = await res.json();
        if (!res.ok) { setError(json.error || 'Failed to delete auth user.'); setDeleteLoading(false); setDeleteTarget(null); return; }
      }
      await supabase.from('staff').delete().eq('id', deleteTarget.id);
      setSuccess(`${deleteTarget.display_name} deleted.`);
      setDeleteTarget(null);
      load();
    } catch (e) { setError(String(e)); }
    setDeleteLoading(false);
  }

  const ROLE_OPTIONS = [
    { value: 'staff',  label: 'Staff' },
    { value: 'admin',  label: 'Admin' },
    { value: 'sadmin', label: 'Super Admin' },
    { value: 'agent',  label: 'Agent' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Users</div>
          <div className="page-subtitle">Staff accounts and access</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowAdd(true); setError(''); setSuccess(''); }}>+ Add User</button>
      </div>

      {success && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#166534', fontSize: 13 }}>
          {success}
        </div>
      )}

      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 400, padding: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Delete User</div>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
              Permanently delete <strong>{deleteTarget.display_name}</strong> ({deleteTarget.email.replace('@tls.internal', '')})?
              This removes their login access. This cannot be undone.
            </p>
            {error && <div style={{ color: 'var(--danger)', marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting…' : 'Yes, Delete'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setDeleteTarget(null); setError(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="card" style={{ marginBottom: 20, padding: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>New User</div>
          {error && <div style={{ color: 'var(--danger)', marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <input className="form-input" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="John Silva" />
            </div>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input className="form-input" type="text" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="e.g. lakmali" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 characters" />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-input" value={form.role} onChange={e => {
                const r = e.target.value;
                setForm(f => ({ ...f, role: r, agent_ref_id: '', tab_permissions: ROLE_DEFAULTS[r] || [] }));
              }}>
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Branch</label>
              <select className="form-input" value={form.branch_id} onChange={e => setForm(f => ({ ...f, branch_id: e.target.value }))}>
                <option value="">— All branches —</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
              </select>
            </div>
            {form.role === 'agent' && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Linked Agent Record</label>
                <select className="form-input" value={form.agent_ref_id} onChange={e => setForm(f => ({ ...f, agent_ref_id: e.target.value }))}>
                  <option value="">— Select agent —</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.tls_code} — {a.full_name}</option>)}
                </select>
              </div>
            )}
          </div>

          {form.role !== 'sadmin' && form.role !== 'agent' && (
            <div style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowPerms(p => !p)}>
                {showPerms ? '▲ Hide' : '▼ Show'} Tab Permissions
              </button>
              {showPerms && (
                <div style={{ marginTop: 10, padding: 16, background: 'var(--bg-subtle, #f9fafb)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  <PermissionsChecklist value={form.tab_permissions} onChange={v => setForm(f => ({ ...f, tab_permissions: v }))} />
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowWidgets(p => !p)}>
              {showWidgets ? '▲ Hide' : '▼ Show'} Dashboard Widgets
            </button>
            {showWidgets && (
              <div style={{ marginTop: 10, padding: 16, background: 'var(--bg-subtle, #f9fafb)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <WidgetChecklist value={form.dashboard_widgets} onChange={v => setForm(f => ({ ...f, dashboard_widgets: v }))} liabilityAccounts={liabilityAccounts} />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Creating…' : 'Create User'}</button>
            <button className="btn btn-secondary" onClick={() => { setShowAdd(false); setError(''); setShowPerms(false); setShowWidgets(false); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="spinner" style={{ padding: 24 }} /> : (
          <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Role</th>
                <th>Branch</th>
                <th>Status</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(s => (
                <React.Fragment key={s.id}>
                  <tr>
                    <td style={{ fontWeight: 600 }}>{s.display_name}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13, fontFamily: 'monospace' }}>{s.email.replace('@tls.internal', '')}</td>
                    <td>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {s.role}{s.role === 'agent' && s.agents ? ` · ${s.agents.tls_code}` : ''}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {s.branches ? `${s.branches.name}` : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td>
                      <span className={`badge ${s.is_active ? 'badge-green' : 'badge-gray'}`}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => editId === s.id ? setEditId(null) : startEdit(s)}>
                          {editId === s.id ? 'Cancel' : 'Edit'}
                        </button>
                        <button className={`btn btn-sm ${s.is_active ? 'btn-secondary' : 'btn-primary'}`} onClick={() => toggleActive(s)}>
                          {s.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => { setDeleteTarget(s); setError(''); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>

                  {editId === s.id && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--bg-subtle, #f9fafb)', padding: '16px 20px' }}>
                        {editError && <div style={{ color: 'var(--danger)', marginBottom: 10, fontSize: 13 }}>{editError}</div>}
                        <div style={{ display: 'grid', gridTemplateColumns: role === 'sadmin' ? '1fr 1fr 1.2fr 1fr 1fr 1fr 1fr 1fr 1fr auto' : '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'flex-end' }}>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>Display Name</label>
                            <input className="form-input" value={editForm.display_name}
                              onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))} />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>Username</label>
                            <input className="form-input" type="text" value={editForm.username}
                              onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))} />
                          </div>
                          {role === 'sadmin' && (
                            <div className="form-group" style={{ margin: 0 }}>
                              <label className="form-label" style={{ fontSize: 12 }} title="Their real, personal inbox — used for password reset. Never the internal @tls.internal login address.">Real Email</label>
                              <input className="form-input" type="email" placeholder="name@gmail.com" value={editForm.personal_email || ''}
                                onChange={e => setEditForm(f => ({ ...f, personal_email: e.target.value }))} />
                            </div>
                          )}
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>New Password <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(blank = keep)</span></label>
                            <input className="form-input" type="password" placeholder="••••••"
                              value={editForm.password}
                              onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>Role</label>
                            <select className="form-input" value={editForm.role || s.role}
                              onChange={e => {
                                const r = e.target.value;
                                setEditForm(f => ({ ...f, role: r, agent_ref_id: r !== 'agent' ? null : f.agent_ref_id, tab_permissions: ROLE_DEFAULTS[r] || [] }));
                              }}>
                              {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>Linked Agent</label>
                            <select className="form-input" value={editForm.agent_ref_id || s.agent_ref_id || ''}
                              onChange={e => setEditForm(f => ({ ...f, agent_ref_id: e.target.value || null }))}>
                              <option value="">— none —</option>
                              {agents.map(a => <option key={a.id} value={a.id}>{a.tls_code} — {a.full_name}</option>)}
                            </select>
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>Branch</label>
                            <select className="form-input" value={editForm.branch_id || ''}
                              onChange={e => setEditForm(f => ({ ...f, branch_id: e.target.value || null }))}>
                              <option value="">— All —</option>
                              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>Admin Passcode <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(blank = keep)</span></label>
                            <input className="form-input" type="password" placeholder="••••••"
                              value={editForm.action_passcode || ''}
                              onChange={e => setEditForm(f => ({ ...f, action_passcode: e.target.value }))} />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: 12 }}>Void PIN <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(blank = keep)</span></label>
                            <input className="form-input" type="password" placeholder="••••••"
                              value={editForm.void_passcode || ''}
                              onChange={e => setEditForm(f => ({ ...f, void_passcode: e.target.value }))} />
                          </div>
                          <button className="btn btn-primary btn-sm" onClick={() => saveEdit(s)} disabled={editSaving}>
                            {editSaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>

                        {(editForm.role || s.role) !== 'sadmin' && (editForm.role || s.role) !== 'agent' && (
                          <div style={{ marginTop: 14 }}>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowPerms(p => !p)}>
                              {showPerms ? '▲ Hide' : '▼ Show'} Tab Permissions
                            </button>
                            {showPerms && (
                              <div style={{ marginTop: 10, padding: 16, background: 'white', borderRadius: 8, border: '1px solid var(--border)' }}>
                                <PermissionsChecklist
                                  value={editForm.tab_permissions || []}
                                  onChange={v => setEditForm(f => ({ ...f, tab_permissions: v }))}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ marginTop: 10 }}>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowWidgets(p => !p)}>
                            {showWidgets ? '▲ Hide' : '▼ Show'} Dashboard Widgets
                          </button>
                          {showWidgets && (
                            <div style={{ marginTop: 10, padding: 16, background: 'white', borderRadius: 8, border: '1px solid var(--border)' }}>
                              <WidgetChecklist
                                value={editForm.dashboard_widgets || []}
                                onChange={v => setEditForm(f => ({ ...f, dashboard_widgets: v }))}
                                liabilityAccounts={liabilityAccounts}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {staff.length === 0 && (
                <tr><td colSpan={5}><div className="empty-state"><div className="empty-state-title">No users yet</div></div></td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
